// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { AuthCard, AuthScene, CheckIcon, Stepper } from '../components/authui';
import { SignedInAs, type DnsRecord } from './OnboardingGate';

// Domain verification: the tenant is pending until the founder proves control of the domain
// via a DNS TXT record. We show the record; the backend does the authoritative-NS lookup +
// activation on Verify. On success we refresh the session (new token carries tenant_id) and
// drop into the app.

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`copy-btn ${copied ? 'done' : ''}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

export function VerifyDomainScreen({ domain, dnsRecord }: { domain?: string; dnsRecord?: DnsRecord }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function verify() {
    setBusy(true);
    setHint(null);
    setError(null);
    try {
      const res = await api.request<{ state?: string; ok?: boolean; hint?: string }>('/v1/onboarding/verify', { method: 'POST' });
      if (res.state === 'active') {
        setDone(true);
        // Tenant state lives in the DB and is read via GET /v1/onboarding — invalidate so the
        // gate re-fetches and drops us into the app. (No token refresh: the token carries no
        // tenant claim, and a failed refresh here used to strand the user on this screen.)
        await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
        setTimeout(() => navigate('/', { replace: true }), 900);
        return;
      }
      setHint(res.hint ?? 'Not found yet — DNS can take a few minutes to propagate. We check your domain’s nameservers directly, so it’s usually quick.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthScene>
        <AuthCard>
          <div style={{ textAlign: 'center', padding: 'var(--sp-3) 0' }}>
            <div className="success-check"><CheckIcon /></div>
            <h1 style={{ fontSize: 'var(--fs-20)' }}>Domain verified</h1>
            <p className="muted" style={{ marginTop: 6 }}>Taking you to your workspace…</p>
          </div>
        </AuthCard>
      </AuthScene>
    );
  }

  return (
    <AuthScene>
      <AuthCard footer={<SignedInAs />}>
        <Stepper current={2} />
        <div className="auth-head">
          <h1>{domain ? <>Verify {domain}</> : 'Verify your domain'}</h1>
          <p>Add one DNS record to prove you control the domain, then activate your workspace.</p>
        </div>

        {dnsRecord ? (
          <>
            <p className="muted" style={{ fontSize: 'var(--fs-13)' }}>
              In your DNS provider, add this <strong>TXT</strong> record:
            </p>
            <div className="dns-card">
              <div className="dns-row"><span className="k">Type</span><span className="v">TXT</span><span /></div>
              <div className="dns-row"><span className="k">Host</span><span className="v">{dnsRecord.host}</span><CopyButton text={dnsRecord.host} /></div>
              <div className="dns-row"><span className="k">Value</span><span className="v">{dnsRecord.value}</span><CopyButton text={dnsRecord.value} /></div>
            </div>
          </>
        ) : (
          <p className="muted" style={{ fontSize: 'var(--fs-13)' }}>
            Your organization is pending domain verification. Once your DNS record is in place, verify below.
          </p>
        )}

        <button className="btn primary block lift" style={{ marginTop: 'var(--sp-4)' }} onClick={() => void verify()} disabled={busy}>
          {busy ? 'Checking DNS…' : 'Verify domain'}
        </button>

        {busy && (
          <div className="checking">
            <span className="dots"><span /><span /><span /></span> Querying your domain’s nameservers…
          </div>
        )}
        {hint && <div className="auth-msg info" style={{ marginTop: 'var(--sp-2)' }}>ⓘ {hint}</div>}
        {error && <div className="auth-msg err" style={{ marginTop: 'var(--sp-2)' }}>⚠ {error}</div>}
      </AuthCard>
    </AuthScene>
  );
}
